// US-AUTH-07 -- Settings screen with PIN management.
//
// US-AUTH-10 (2026-04-25): the screen is now reachable by both
// guardians and children -- children need it to manage their own PIN.
// Children see PIN-only; guardians see the admin panels too.
//
// US-SETUP-06 (2026-04-25): Settings is now the consolidated home
// for guardian admin actions. Three new panels render for guardians:
//   - Manage users (Children + Guardians sub-sections, each with an
//     inline + Add button and the shared GBP/USD family currency)
//   - About
// Children see only the PIN panel -- admin panels are gated behind
// `<Show when={roleCode === 'guardian'}>`.

import {
  type AccountListEntry,
  type AppError,
  type RecoverableChildEntry,
  type RecoverableGuardianEntry,
  accountsList,
  biometricDisableForAccount,
  biometricEnableForAccount,
  biometricStatusForAccount,
  biometricSupport,
  childRecoveryList,
  childRestore,
  familyCurrencyUpdate,
  familyDataDeleteLocal,
  guardianChallenge,
  guardianDeactivate,
  guardianRecoveryList,
  guardianRestore,
  pinRemove,
  pinSet,
  pinStatusForSession,
} from '@chorepop/core/client';
import { type Component, For, Show, createEffect, createResource, createSignal } from 'solid-js';
import { playRewardSound } from '../audio/rewardJingle';
import {
  cancelFamilyDataDeletion,
  requestFamilyDataDeletion,
  updateCloudFamilyCurrencyIfLinked,
} from '../cloud/cloudAuth';
import { ActionScreen } from '../components/ActionScreen';
import { FamilyDataDownloadPanel } from '../components/FamilyDataDownloadPanel';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { TransientBanner } from '../components/TransientBanner';
import {
  highContrastEnabled,
  largeFontEnabled,
  setHighContrastEnabled,
  setLargeFontEnabled,
} from '../state/a11yPreferences';
import {
  MONEY_SOUND_OPTIONS,
  type MoneySoundId,
  coinJinglesEnabled,
  confettiBurstsEnabled,
  goalStarBurstsEnabled,
  moneySound,
  setCoinJinglesEnabled,
  setConfettiBurstsEnabled,
  setGoalStarBurstsEnabled,
  setMoneySound,
} from '../state/childCelebrationPreferences';
import { clearAllFamilyContentVisibility } from '../state/familyContentVisibility';
import {
  isViewportWideEnoughForMobilePreview,
  mobilePreviewEnabled,
  setMobilePreviewEnabled,
} from '../state/mobilePreview';
import { PALETTES, palette, setPalette } from '../state/palettePreference';
import { setThemeMode, themeMode } from '../state/themePreference';

type Props = {
  sessionId: string;
  accountId: number;
  displayName: string;
  /**
   * Role of the signed-in account. Children see PIN-only; guardians
   * see the full admin panel set introduced by US-SETUP-06.
   */
  roleCode: 'guardian' | 'child';
  familyCurrencyCode?: 'GBP' | 'USD';
  onClose: () => void;
  /**
   * US-SETUP-06 -- guardian-only entry points. Optional so the
   * component can still render in test scaffolds that don't wire
   * them. App.tsx wires all three to the existing route variants
   * (no new routes added by this story).
   */
  onAddChild?: (() => void) | undefined;
  onAddGuardian?: (() => void) | undefined;
  onFamilyDeletionRequested?: ((deleteAfter: string) => void) | undefined;
  onFamilyCurrencyChanged?: ((newCurrencyCode: 'GBP' | 'USD') => void) | undefined;
};

export const SettingsScreen: Component<Props> = (props) => {
  const [hasPin, { refetch: refetchHasPin }] = createResource(async () => {
    const r = await pinStatusForSession({ sessionId: props.sessionId });
    if (!r.ok) return false;
    return r.value.hasPin;
  });

  return (
    <ActionScreen
      eyebrow={props.roleCode === 'guardian' ? 'Parent account' : 'Your account'}
      title="Settings"
      onBack={props.onClose}
      class="action-screen--settings"
    >
      <div class="settings-card">
        {/* US-UI-MOBILE-PREVIEW -- Display panel. The toggle is only
            shown when the viewport is wider than the mobile breakpoint
            (>600px); on a real mobile device the toggle is hidden,
            since there's nothing to preview. The frame itself is
            applied at App.tsx root via a module-level signal so it
            wraps every route. */}
        {/* Display panel -- always rendered. Includes:
            * US-PLATFORM-03 a11y toggles (high-contrast + large-font),
              available to anyone signed in, on any viewport size.
            * US-UI-MOBILE-PREVIEW toggle -- gated to wide viewports
              (>600px) since on a real mobile device there's nothing
              to preview. */}
        <section class="settings-section">
          <h2>Display</h2>

          {/* US-PLATFORM-03 -- high-contrast toggle. Drives a
              data-a11y-contrast="high" attribute on the App.tsx
              root wrapper; CSS in styles.css picks it up and
              increases colour contrast app-wide. */}
          <ToggleSwitch
            label="High-contrast mode"
            checked={highContrastEnabled()}
            onChange={setHighContrastEnabled}
            testId="settings-high-contrast-toggle"
            describedBy="settings-high-contrast-help"
          />
          <p
            id="settings-high-contrast-help"
            class="settings-section-intro"
            style="margin: 0 0 8px 28px; font-size: 12px;"
          >
            Increases colour contrast for easier reading.
          </p>

          {/* US-PLATFORM-03 -- large-font toggle. Drives
              data-a11y-font-size="large"; CSS bumps the base
              font size by 25% app-wide. */}
          <ToggleSwitch
            label="Large font mode"
            checked={largeFontEnabled()}
            onChange={setLargeFontEnabled}
            testId="settings-large-font-toggle"
            describedBy="settings-large-font-help"
          />
          <p
            id="settings-large-font-help"
            class="settings-section-intro"
            style="margin: 0 0 8px 28px; font-size: 12px;"
          >
            Increases the base font size by 25% across the app.
          </p>

          {/* US-PLATFORM-07 -- dark-mode toggle. Drives
              data-theme="light|dark" on the App.tsx root wrapper;
              CSS in styles/app.css overrides the design-token
              colours when dark. Orthogonal to high-contrast: when
              both are on, high-contrast wins (per the spec -- the
              user has explicitly asked for maximum contrast). */}
          <ToggleSwitch
            label="Dark mode"
            checked={themeMode() === 'dark'}
            onChange={(enabled) => setThemeMode(enabled ? 'dark' : 'light')}
            testId="settings-dark-mode-toggle"
            describedBy="settings-dark-mode-help"
          />
          <p
            id="settings-dark-mode-help"
            class="settings-section-intro"
            style="margin: 0 0 8px 28px; font-size: 12px;"
          >
            Switches the app to a dark colour scheme. High-contrast mode (above) overrides this
            setting if both are on.
          </p>

          {/* US-CUSTOM-02 -- Household colour palette */}
          <div style="margin-top: var(--space-3);">
            <p style="margin: 0 0 var(--space-2); font-size: 0.88rem; font-weight: 600;">
              Colour theme
            </p>
            <div class="palette-picker" data-testid="palette-picker">
              <For each={PALETTES}>
                {(opt) => (
                  <button
                    type="button"
                    class="palette-btn"
                    classList={{ 'palette-btn--on': palette() === opt.id }}
                    onClick={() => setPalette(opt.id)}
                    aria-pressed={palette() === opt.id}
                    data-testid={`palette-btn-${opt.id}`}
                  >
                    <span class="palette-swatch" style={`background: ${opt.preview}`} />
                    <span>{opt.label}</span>
                  </button>
                )}
              </For>
            </div>
          </div>

          {/* US-UI-MOBILE-PREVIEW -- toggle hidden on viewports
              ≤ 600px (no reverse scenario on real mobile). */}
          <Show when={isViewportWideEnoughForMobilePreview()}>
            <ToggleSwitch
              label="Preview as mobile (390 × 844 frame)"
              checked={mobilePreviewEnabled()}
              onChange={setMobilePreviewEnabled}
              testId="settings-mobile-preview-toggle"
              describedBy="settings-mobile-preview-help"
            />
            <p
              id="settings-mobile-preview-help"
              class="settings-section-intro"
              style="margin: 0 0 8px 28px; font-size: 12px;"
            >
              Useful when you want to check how a screen looks on a phone without leaving your
              desktop.
            </p>
          </Show>
        </section>

        <Show when={props.roleCode === 'child'}>
          <section class="settings-section" aria-label="Fun settings">
            <h2>Fun settings</h2>
            <p class="settings-section-intro">
              Choose the celebrations you like. These choices affect only your ChorePop account on
              this device.
            </p>
            <ToggleSwitch
              label="Confetti burst after I finish a chore"
              checked={confettiBurstsEnabled()}
              onChange={setConfettiBurstsEnabled}
              testId="settings-confetti-toggle"
            />
            <ToggleSwitch
              label="Star burst when I finish a goal"
              checked={goalStarBurstsEnabled()}
              onChange={setGoalStarBurstsEnabled}
              testId="settings-goal-stars-toggle"
            />
            <ToggleSwitch
              label="Coin jingle when money arrives"
              checked={coinJinglesEnabled()}
              onChange={setCoinJinglesEnabled}
              testId="settings-coin-jingle-toggle"
            />
            <label class="settings-sound-choice" for="settings-money-sound">
              <span>Money sound</span>
              <select
                id="settings-money-sound"
                value={moneySound()}
                disabled={!coinJinglesEnabled()}
                onChange={(event) => setMoneySound(event.currentTarget.value as MoneySoundId)}
                data-testid="settings-money-sound-select"
              >
                <For each={MONEY_SOUND_OPTIONS}>
                  {(option) => <option value={option.id}>{option.label}</option>}
                </For>
              </select>
              <small>
                {MONEY_SOUND_OPTIONS.find((option) => option.id === moneySound())?.description}
              </small>
            </label>
            <button
              type="button"
              class="ghost settings-sound-preview"
              disabled={!coinJinglesEnabled()}
              onClick={() => playRewardSound(moneySound())}
              data-testid="settings-coin-jingle-preview"
            >
              Play this sound
            </button>
          </section>
        </Show>

        <section class="settings-section">
          <h2>Sign-in PIN</h2>
          <p class="settings-section-intro">
            A 6-digit PIN is a quick way to unlock this account on this device. Your password is
            always available as a guardian fallback. To connect a different device to your family,
            use the same Google or Apple account you used for cloud sign-in.
          </p>

          <Show when={!hasPin.loading}>
            <Show
              when={hasPin()}
              fallback={
                <PinSetPanel
                  mode="set"
                  sessionId={props.sessionId}
                  onSaved={() => refetchHasPin()}
                />
              }
            >
              {/* BUG-022: when a PIN is set, surface a "Change PIN"
                  affordance alongside the existing "Remove PIN" button.
                  Change is implemented as a remove-then-set under the
                  hood -- no new backend command needed, and the user is
                  already authenticated to be in Settings so a current-PIN
                  re-prompt is unnecessary. The two panels render together
                  with a visual separator between them. */}
              <PinSetPanel
                mode="change"
                sessionId={props.sessionId}
                onSaved={() => refetchHasPin()}
              />
              <hr class="settings-section-divider" />
              <PinRemovePanel sessionId={props.sessionId} onRemoved={() => refetchHasPin()} />
            </Show>
          </Show>
        </section>

        <Show when={props.roleCode === 'guardian'}>
          <MainAdultBiometricPanel sessionId={props.sessionId} currentAccountId={props.accountId} />
        </Show>

        {/* Guardian-only household administration. Local pairing and manual
            device controls are deliberately not user-facing settings in
            ChorePop. */}
        <Show when={props.roleCode === 'guardian'}>
          <FamilyCurrencyPanel
            sessionId={props.sessionId}
            currentCurrencyCode={props.familyCurrencyCode ?? 'GBP'}
            onChanged={(currency) => props.onFamilyCurrencyChanged?.(currency)}
          />

          <ManageUsersPanel
            sessionId={props.sessionId}
            currentAccountId={props.accountId}
            onAddChild={props.onAddChild}
            onAddGuardian={props.onAddGuardian}
          />

          <RecentlyDeletedChildrenPanel sessionId={props.sessionId} />
          <RecentlyDeletedGuardiansPanel sessionId={props.sessionId} />

          <FamilyDataDownloadPanel sessionId={props.sessionId} />

          <FamilyPrivacyPanel
            accountId={props.accountId}
            displayName={props.displayName}
            sessionId={props.sessionId}
            onDeletionRequested={(deleteAfter) => props.onFamilyDeletionRequested?.(deleteAfter)}
          />
        </Show>
      </div>
    </ActionScreen>
  );
};

export const MainAdultBiometricPanel: Component<{
  sessionId: string;
  currentAccountId: number;
}> = (props) => {
  const [support] = createResource(async () => {
    const result = await biometricSupport();
    return result.ok ? result.value : null;
  });
  const [guardians] = createResource(async () => {
    const result = await accountsList();
    return result.ok ? result.value.filter((account) => account.roleCode === 'guardian') : [];
  });
  const [enabledAccountId, { refetch: refetchEnabledAccount }] = createResource(
    guardians,
    async (adultAccounts) => {
      const statuses = await Promise.all(
        adultAccounts.map(async (account) => ({
          accountId: account.accountId,
          status: await biometricStatusForAccount({ accountId: account.accountId }),
        })),
      );
      return (
        statuses.find((entry) => entry.status.ok && entry.status.value.enabled)?.accountId ?? null
      );
    },
  );
  const [selectedAccountId, setSelectedAccountId] = createSignal(props.currentAccountId);
  const [pin, setPin] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [message, setMessage] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  createEffect(() => {
    const enrolled = enabledAccountId();
    if (enrolled !== null && enrolled !== undefined) setSelectedAccountId(enrolled);
  });

  const selectedGuardian = () =>
    (guardians() ?? []).find((guardian) => guardian.accountId === selectedAccountId());
  const biometryLabel = () => support()?.biometryLabel ?? 'biometrics';

  const enable = async (event: Event) => {
    event.preventDefault();
    setMessage(null);
    setError(null);
    if (!/^\d{6}$/.test(pin())) {
      setError("Enter the selected adult's 6-digit PIN.");
      return;
    }
    setBusy(true);
    try {
      const result = await biometricEnableForAccount({
        sessionId: props.sessionId,
        accountId: selectedAccountId(),
        pin: pin(),
      });
      setPin('');
      if (!result.ok) {
        setError(humanReadableError(result.error));
        return;
      }
      await refetchEnabledAccount();
      setMessage(
        `${selectedGuardian()?.displayName ?? 'This adult'} is now the main adult for ${biometryLabel()} sign-in on this device.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await biometricDisableForAccount({
        sessionId: props.sessionId,
        accountId: selectedAccountId(),
      });
      if (!result.ok) {
        setError(humanReadableError(result.error));
        return;
      }
      await refetchEnabledAccount();
      setMessage(
        'Biometric sign-in is off on this device. Adult PIN and password sign-in still work.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      class="settings-section settings-biometric-section"
      aria-label="Main adult biometric sign-in"
    >
      <h2>Main adult biometric sign-in</h2>
      <p class="settings-section-intro">
        Choose one guardian as the main adult on this phone or tablet. Only that adult can use the
        device's face or fingerprint sign-in. Every guardian keeps the same family permissions, and
        children can never use biometrics.
      </p>
      <p class="settings-biometric-security-note">
        Important: anyone whose face or fingerprint is enrolled in this phone or tablet may be
        accepted by its biometric prompt. Only enable this on a device you trust.
      </p>
      <Show when={!support.loading && !guardians.loading}>
        <Show
          when={support()?.support === 'available'}
          fallback={
            <p class="settings-section-intro" data-testid="settings-biometric-unavailable">
              Set up face or fingerprint recognition in this device's settings to use biometric
              sign-in. PIN and password sign-in are still available.
            </p>
          }
        >
          <form onSubmit={enable} class="settings-biometric-form">
            <label class="field" for="settings-main-adult-select">
              <span class="field-label">Main adult on this device</span>
              <select
                id="settings-main-adult-select"
                value={selectedAccountId()}
                disabled={busy()}
                onChange={(event) => {
                  setSelectedAccountId(Number(event.currentTarget.value));
                  setPin('');
                  setMessage(null);
                  setError(null);
                }}
                data-testid="settings-main-adult-select"
              >
                <For each={guardians() ?? []}>
                  {(guardian) => <option value={guardian.accountId}>{guardian.displayName}</option>}
                </For>
              </select>
            </label>
            <Show when={enabledAccountId() === selectedAccountId()}>
              <p class="form-flash" data-testid="settings-biometric-enabled">
                {selectedGuardian()?.displayName} can sign in with {biometryLabel()} on this device.
              </p>
              <button
                type="button"
                class="ghost"
                disabled={busy()}
                onClick={() => void disable()}
                data-testid="settings-biometric-disable"
              >
                {busy() ? 'Turning off…' : 'Turn off biometric sign-in'}
              </button>
            </Show>
            <Show when={enabledAccountId() !== selectedAccountId()}>
              <label class="field" for="settings-main-adult-pin">
                <span class="field-label">{selectedGuardian()?.displayName}'s 6-digit PIN</span>
                <input
                  id="settings-main-adult-pin"
                  type="password"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxLength={6}
                  value={pin()}
                  disabled={busy()}
                  onInput={(event) => {
                    const value = event.currentTarget.value.replace(/\D/g, '').slice(0, 6);
                    setPin(value);
                    event.currentTarget.value = value;
                    setError(null);
                  }}
                  data-testid="settings-main-adult-pin"
                />
                <span class="field-hint">
                  We verify this PIN before the device asks for {biometryLabel()}.
                </span>
              </label>
              <button
                type="submit"
                class="primary"
                disabled={busy() || pin().length !== 6}
                data-testid="settings-biometric-enable"
              >
                {busy() ? 'Checking…' : `Use ${biometryLabel()} for this adult`}
              </button>
            </Show>
          </form>
        </Show>
      </Show>
      <Show when={message()}>{(text) => <p class="form-flash">{text()}</p>}</Show>
      <Show when={error()}>
        {(text) => (
          <p class="form-error" role="alert">
            {text()}
          </p>
        )}
      </Show>
    </section>
  );
};

export const FamilyPrivacyPanel: Component<{
  accountId: number;
  displayName: string;
  sessionId: string;
  onDeletionRequested: (deleteAfter: string) => void;
}> = (props) => {
  const [expanded, setExpanded] = createSignal(false);
  const [confirmation, setConfirmation] = createSignal('');
  const [credential, setCredential] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const phrase = () => `DELETE ${props.displayName}`;

  const submit = async (event: Event) => {
    event.preventDefault();
    setError(null);
    if (confirmation().trim() !== phrase()) {
      setError(`Type ${phrase()} exactly to continue.`);
      return;
    }
    if (!credential()) {
      setError('Enter your guardian password or 6-digit PIN.');
      return;
    }
    setBusy(true);
    try {
      const challenge = await guardianChallenge({
        accountId: props.accountId,
        credential: /^\d{6}$/.test(credential())
          ? { kind: 'pin', pin: credential() }
          : { kind: 'password', password: credential() },
      });
      setCredential('');
      if (!challenge.ok) {
        setError(challenge.error.message);
        return;
      }
      if (challenge.value.outcome !== 'success') {
        setError(
          challenge.value.outcome === 'locked'
            ? 'Guardian verification is temporarily locked. Try again later.'
            : 'That guardian credential was not accepted.',
        );
        return;
      }
      const request = await requestFamilyDataDeletion();
      const localDelete = await familyDataDeleteLocal({ sessionId: props.sessionId });
      if (!localDelete.ok) {
        try {
          await cancelFamilyDataDeletion(request.familyId);
        } catch {
          throw new Error(
            'The server locked the family data, but this device could not remove its local copy. Sign out now and contact ChorePop support.',
          );
        }
        throw new Error(`Could not remove this device's local data: ${localDelete.error.message}`);
      }
      clearAllFamilyContentVisibility();
      props.onDeletionRequested(request.deleteAfter);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not schedule data deletion.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="settings-section settings-privacy-section" aria-label="Privacy and data">
      <h2>Privacy and data</h2>
      <p class="settings-section-intro">
        Permanently delete this family and all of its data after a 30-day recovery period. The
        permanent purge runs only on the ChorePop server after that deadline; this app never
        receives purge credentials.
      </p>
      <Show
        when={expanded()}
        fallback={
          <button
            type="button"
            class="danger"
            onClick={() => setExpanded(true)}
            data-testid="delete-all-family-data-open"
          >
            Delete this family and all family data
          </button>
        }
      >
        <form onSubmit={submit} class="privacy-delete-form">
          <div class="form-error" role="alert">
            This signs everyone out and schedules the entire family ledger, guardian and child
            accounts, buckets, chores, goals, and history for permanent deletion. Either guardian
            can cancel it during the 30-day recovery period.
          </div>
          <label class="field">
            <span class="field-label">
              Type <strong>{phrase()}</strong>
            </span>
            <input
              type="text"
              autocomplete="off"
              value={confirmation()}
              onInput={(event) => setConfirmation(event.currentTarget.value)}
              disabled={busy()}
              data-testid="delete-all-family-data-confirmation"
            />
          </label>
          <label class="field">
            <span class="field-label">Guardian password or PIN</span>
            <input
              type="password"
              autocomplete="current-password"
              value={credential()}
              onInput={(event) => setCredential(event.currentTarget.value)}
              disabled={busy()}
              data-testid="delete-all-family-data-credential"
            />
          </label>
          <Show when={error()}>
            {(message) => (
              <p class="form-error" role="alert">
                {message()}
              </p>
            )}
          </Show>
          <div class="privacy-delete-actions">
            <button
              type="button"
              class="ghost"
              disabled={busy()}
              onClick={() => {
                setExpanded(false);
                setConfirmation('');
                setCredential('');
                setError(null);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              class="danger"
              disabled={busy() || confirmation().trim() !== phrase() || credential().length === 0}
              data-testid="delete-all-family-data-submit"
            >
              {busy() ? 'Verifying…' : 'Schedule deletion'}
            </button>
          </div>
        </form>
      </Show>
    </section>
  );
};

// ---- Set / Change PIN panel ------------------------------------------------
//
// Single component used for both "set" (no PIN exists yet) and "change"
// (PIN exists, user wants a different one). The form fields are
// identical; only the labels and the submit handler differ. For
// `mode='change'` the handler calls `pinRemove` followed by `pinSet`
// so the existing PIN row is replaced atomically from the user's POV.

const PinSetPanel: Component<{
  mode: 'set' | 'change';
  sessionId: string;
  onSaved: () => void;
}> = (props) => {
  const [pin1, setPin1] = createSignal('');
  const [pin2, setPin2] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);
  const [flash, setFlash] = createSignal<string | null>(null);
  // `expanded` only applies to change mode -- the form is collapsed by
  // default behind a "Change PIN" button to avoid two competing forms
  // (set + change) showing at once. In set mode the form is always
  // visible because there's nothing else to do in the panel.
  const [expanded, setExpanded] = createSignal(props.mode === 'set');

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErr(null);
    setFlash(null);

    if (!/^\d{6}$/.test(pin1())) {
      setErr('PIN must be exactly 6 digits.');
      return;
    }
    if (pin1() !== pin2()) {
      setErr("The two PINs don't match. Please re-enter.");
      return;
    }

    setSubmitting(true);
    try {
      // BUG-022: change-mode does remove-then-set. The user is already
      // signed in via session, so no current-PIN re-prompt is needed.
      // If the remove succeeds and the set fails (rare -- usually only
      // a transport/db error), the user ends up with no PIN, which is
      // a recoverable state (they can set a fresh PIN on the next try).
      if (props.mode === 'change') {
        const removeResult = await pinRemove({ sessionId: props.sessionId });
        if (!removeResult.ok) {
          setErr(humanReadableError(removeResult.error));
          return;
        }
      }
      const r = await pinSet({ sessionId: props.sessionId, pin: pin1() });
      if (!r.ok) {
        setErr(humanReadableError(r.error));
        return;
      }
      setPin1('');
      setPin2('');
      setFlash(
        props.mode === 'change'
          ? 'PIN updated. Use your new PIN to sign in next time.'
          : 'PIN saved. You can now sign in with your PIN on this device.',
      );
      if (props.mode === 'change') {
        setExpanded(false);
      }
      props.onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Show
      when={expanded()}
      fallback={
        <div class="pin-change-collapsed" data-testid="pin-change-collapsed">
          <p class="pin-change-status">A PIN is set on this device.</p>
          <button
            type="button"
            class="secondary"
            onClick={() => {
              setExpanded(true);
              setFlash(null);
              setErr(null);
            }}
            data-testid="pin-change-open"
          >
            Change PIN
          </button>
          <Show when={flash()}>
            {(m) => (
              <TransientBanner
                class="form-flash"
                message={m()}
                testId="pin-change-flash"
                onDismiss={() => setFlash(null)}
              />
            )}
          </Show>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit}
        novalidate
        data-testid={props.mode === 'change' ? 'pin-change-form' : 'pin-set-form'}
      >
        <label class="field">
          <span class="field-label">
            {props.mode === 'change' ? 'Enter a new 6-digit PIN' : 'Enter a 6-digit PIN'}
          </span>
          <input
            type="password"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            class="pin-input"
            disabled={submitting()}
            value={pin1()}
            onInput={(e) => {
              const cleaned = e.currentTarget.value.replace(/\D/g, '').slice(0, 6);
              setPin1(cleaned);
              e.currentTarget.value = cleaned;
              setErr(null);
            }}
            data-testid="pin-set-input-1"
          />
        </label>

        <label class="field">
          <span class="field-label">Confirm PIN</span>
          <input
            type="password"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            class="pin-input"
            disabled={submitting()}
            value={pin2()}
            onInput={(e) => {
              const cleaned = e.currentTarget.value.replace(/\D/g, '').slice(0, 6);
              setPin2(cleaned);
              e.currentTarget.value = cleaned;
              setErr(null);
            }}
            data-testid="pin-set-input-2"
          />
        </label>

        <Show when={err()}>
          {(m) => (
            <div class="form-error" role="alert" data-testid="pin-set-error">
              {m()}
            </div>
          )}
        </Show>

        <Show when={flash()}>
          {(m) => (
            <TransientBanner
              class="form-flash"
              message={m()}
              testId="pin-set-flash"
              onDismiss={() => setFlash(null)}
            />
          )}
        </Show>

        <div class="pin-form-actions">
          <button
            type="submit"
            class="primary"
            disabled={submitting() || pin1().length !== 6 || pin2().length !== 6}
            data-testid={props.mode === 'change' ? 'pin-change-submit' : 'pin-set-submit'}
          >
            {submitting()
              ? props.mode === 'change'
                ? 'Updating…'
                : 'Saving…'
              : props.mode === 'change'
                ? 'Update PIN'
                : 'Set PIN'}
          </button>
          <Show when={props.mode === 'change'}>
            <button
              type="button"
              class="ghost"
              disabled={submitting()}
              onClick={() => {
                setExpanded(false);
                setPin1('');
                setPin2('');
                setErr(null);
              }}
              data-testid="pin-change-cancel"
            >
              Cancel
            </button>
          </Show>
        </div>
      </form>
    </Show>
  );
};

// ---- Remove PIN panel ------------------------------------------------------

const PinRemovePanel: Component<{
  sessionId: string;
  onRemoved: () => void;
}> = (props) => {
  const [confirming, setConfirming] = createSignal(false);
  const [submitting, setSubmitting] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);
  const [flash, setFlash] = createSignal<string | null>(null);

  const doRemove = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const r = await pinRemove({ sessionId: props.sessionId });
      if (!r.ok) {
        setErr(humanReadableError(r.error));
        return;
      }
      setFlash('PIN removed. You will sign in with your password on this device.');
      setConfirming(false);
      props.onRemoved();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div class="pin-remove-panel" data-testid="pin-remove-panel">
      <p class="pin-remove-status">A PIN is set on this device.</p>

      <Show
        when={confirming()}
        fallback={
          <button
            type="button"
            class="secondary"
            onClick={() => setConfirming(true)}
            disabled={submitting()}
            data-testid="pin-remove-open"
          >
            Remove PIN
          </button>
        }
      >
        <p class="pin-remove-confirm">
          Remove the PIN for this device? You'll sign in with your password next time.
        </p>
        <div class="pin-remove-actions">
          <button
            type="button"
            class="primary pin-remove-primary"
            onClick={() => void doRemove()}
            disabled={submitting()}
            data-testid="pin-remove-confirm"
          >
            {submitting() ? 'Removing…' : 'Yes, remove it'}
          </button>
          <button
            type="button"
            class="secondary"
            onClick={() => setConfirming(false)}
            disabled={submitting()}
            data-testid="pin-remove-cancel"
          >
            Cancel
          </button>
        </div>
      </Show>

      <Show when={err()}>
        {(m) => (
          <div class="form-error" role="alert" data-testid="pin-remove-error">
            {m()}
          </div>
        )}
      </Show>

      <Show when={flash()}>
        {(m) => (
          <TransientBanner
            class="form-flash"
            message={m()}
            testId="pin-remove-flash"
            onDismiss={() => setFlash(null)}
          />
        )}
      </Show>
    </div>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'not_found':
      return error.message;
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}

const FamilyCurrencyPanel: Component<{
  sessionId: string;
  currentCurrencyCode: 'GBP' | 'USD';
  onChanged: (currency: 'GBP' | 'USD') => void;
}> = (props) => {
  const nextCurrency = () => (props.currentCurrencyCode === 'GBP' ? 'USD' : 'GBP');
  const [confirming, setConfirming] = createSignal(false);
  const [confirmation, setConfirmation] = createSignal('');
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const switchCurrency = async () => {
    if (confirmation().trim().toUpperCase() !== nextCurrency()) return;
    setSaving(true);
    setError(null);
    try {
      await updateCloudFamilyCurrencyIfLinked(nextCurrency());
      const result = await familyCurrencyUpdate({
        sessionId: props.sessionId,
        newCurrencyCode: nextCurrency(),
      });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      props.onChanged(nextCurrency());
    } finally {
      setSaving(false);
    }
  };

  return (
    <section class="settings-section" data-testid="settings-family-currency">
      <h2>Family currency</h2>
      <p class="settings-section-intro">
        Everyone currently uses{' '}
        <strong>
          {props.currentCurrencyCode === 'GBP' ? 'pounds sterling (£)' : 'US dollars ($)'}
        </strong>
        . Other currencies are only recorded for overseas gifts and holiday spending.
      </p>
      <Show
        when={confirming()}
        fallback={
          <button type="button" class="secondary" onClick={() => setConfirming(true)}>
            Switch everyone&apos;s currency
          </button>
        }
      >
        <div class="settings-confirm-card">
          <p>
            This changes every guardian and child, including recently deleted accounts. Amounts are
            not converted:{' '}
            {props.currentCurrencyCode === 'GBP' ? '£10 becomes $10' : '$10 becomes £10'}.
          </p>
          <label class="field">
            <span class="field-label">Type {nextCurrency()} to confirm</span>
            <input
              value={confirmation()}
              disabled={saving()}
              autocomplete="off"
              onInput={(event) => setConfirmation(event.currentTarget.value)}
            />
          </label>
          <Show when={error()}>
            {(message) => (
              <div class="form-error" role="alert">
                {message()}
              </div>
            )}
          </Show>
          <div class="settings-inline-actions">
            <button
              type="button"
              class="primary"
              disabled={saving() || confirmation().trim().toUpperCase() !== nextCurrency()}
              onClick={() => void switchCurrency()}
            >
              {saving() ? 'Switching…' : `Switch everyone to ${nextCurrency()}`}
            </button>
            <button
              type="button"
              class="secondary"
              disabled={saving()}
              onClick={() => {
                setConfirming(false);
                setConfirmation('');
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Show>
    </section>
  );
};

// ---- US-SETUP-06: Manage users panel --------------------------------------
//
// Single panel that lists both children and guardians with role-grouped
// sub-sections. Each sub-section ends with an inline + Add button that
// routes to the existing AddChildScreen / AddGuardianScreen flows -- this
// story is purely a UX relocation, no new commands or migrations.
//
// Account rows show the one shared family currency. The separate family panel
// switches every account together; this list never offers per-person editing.

const ManageUsersPanel: Component<{
  sessionId: string;
  /** AccountId of the signed-in guardian. Peer-deletion controls must never
   * appear on that guardian's own row. */
  currentAccountId: number;
  onAddChild?: (() => void) | undefined;
  onAddGuardian?: (() => void) | undefined;
}> = (props) => {
  const [accounts, { refetch: refetchAccounts }] = createResource(async () => {
    const r = await accountsList();
    if (!r.ok) return [] as AccountListEntry[];
    return r.value;
  });

  const children = () => (accounts() ?? []).filter((a) => a.roleCode === 'child');
  const guardians = () => (accounts() ?? []).filter((a) => a.roleCode === 'guardian');
  const [guardianCanManageRecoverableAccounts] = createResource(async () => {
    const result = await guardianRecoveryList({ sessionId: props.sessionId });
    return result.ok;
  });
  const [guardianToDelete, setGuardianToDelete] = createSignal<AccountListEntry | null>(null);
  const [guardianConfirmationName, setGuardianConfirmationName] = createSignal('');
  const [deletingGuardian, setDeletingGuardian] = createSignal(false);
  const [guardianDeleteError, setGuardianDeleteError] = createSignal<string | null>(null);

  const closeGuardianDelete = () => {
    if (deletingGuardian()) return;
    setGuardianToDelete(null);
    setGuardianConfirmationName('');
    setGuardianDeleteError(null);
  };

  const deleteGuardian = async () => {
    const guardian = guardianToDelete();
    if (!guardian || guardianConfirmationName().trim() !== guardian.displayName) return;
    setDeletingGuardian(true);
    setGuardianDeleteError(null);
    try {
      const result = await guardianDeactivate({
        sessionId: props.sessionId,
        guardianAccountId: guardian.accountId,
      });
      if (!result.ok) {
        setGuardianDeleteError(result.error.message);
        return;
      }
      if (result.value.outcome !== 'success') {
        const messages = {
          not_authorized: 'Only an active guardian can delete another guardian.',
          last_remaining_guardian: 'The last active guardian cannot be deleted.',
          cannot_remove_family_creator: 'This account cannot be deleted right now.',
        };
        setGuardianDeleteError(messages[result.value.outcome]);
        return;
      }
      setGuardianToDelete(null);
      setGuardianConfirmationName('');
      await refetchAccounts();
    } finally {
      setDeletingGuardian(false);
    }
  };

  return (
    <section class="settings-section" data-testid="settings-manage-users">
      <h2>Manage users</h2>
      <p class="settings-section-intro">
        Add or edit the people on this family. The same authority rules apply as everywhere else --
        only guardians can make changes here.
      </p>

      <div class="manage-users-group">
        <h3 class="manage-users-group-heading">Children</h3>
        <Show when={!accounts.loading} fallback={<p class="settings-section-empty">Loading…</p>}>
          <Show
            when={children().length > 0}
            fallback={<p class="settings-section-empty">No children yet.</p>}
          >
            <ul class="manage-users-list">
              <For each={children()}>
                {(c) => (
                  <li class="manage-users-row">
                    <span class="manage-users-avatar" aria-hidden="true">
                      {c.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span class="manage-users-name">{c.displayName}</span>
                    <span class="manage-users-role">CHILD</span>
                    <span class="manage-users-currency-chip">{c.displayCurrencyCode}</span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
          <Show when={props.onAddChild}>
            {(add) => (
              <button
                type="button"
                class="manage-users-add"
                onClick={() => add()()}
                data-testid="settings-add-child"
              >
                <span class="manage-users-add-glyph" aria-hidden="true">
                  +
                </span>
                <span>Add a child</span>
              </button>
            )}
          </Show>
        </Show>
      </div>

      <hr class="settings-section-divider" />

      <div class="manage-users-group">
        <h3 class="manage-users-group-heading">Guardians</h3>
        <Show when={!accounts.loading} fallback={<p class="settings-section-empty">Loading…</p>}>
          <Show
            when={guardians().length > 0}
            fallback={<p class="settings-section-empty">No guardians yet.</p>}
          >
            <ul class="manage-users-list">
              <For each={guardians()}>
                {(g) => (
                  <li class="manage-users-row manage-users-row--guardian">
                    <span
                      class="manage-users-avatar manage-users-avatar--guardian"
                      aria-hidden="true"
                    >
                      {g.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span class="manage-users-name">{g.displayName}</span>
                    <span class="manage-users-role manage-users-role--guardian">GUARDIAN</span>
                    <span class="manage-users-currency-chip">{g.displayCurrencyCode}</span>
                    <Show
                      when={
                        guardianCanManageRecoverableAccounts() === true &&
                        g.accountId !== props.currentAccountId
                      }
                    >
                      <button
                        type="button"
                        class="danger manage-users-delete"
                        onClick={() => {
                          setGuardianToDelete(g);
                          setGuardianConfirmationName('');
                          setGuardianDeleteError(null);
                        }}
                        data-testid={`delete-guardian-${g.accountId}`}
                      >
                        Delete account
                      </button>
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </Show>
          <Show when={guardianToDelete()}>
            {(guardian) => (
              <section
                class="settings-guardian-delete-confirm"
                role="alertdialog"
                aria-labelledby="delete-guardian-title"
              >
                <strong id="delete-guardian-title">
                  Delete {guardian().displayName}'s guardian account?
                </strong>
                <p>
                  They will lose access to this family immediately. Either guardian can restore
                  their account and child relationships for 30 days; after that deletion is
                  permanent.
                </p>
                <label class="field">
                  <span class="field-label">Type {guardian().displayName} to confirm</span>
                  <input
                    type="text"
                    value={guardianConfirmationName()}
                    onInput={(event) => setGuardianConfirmationName(event.currentTarget.value)}
                    autocomplete="off"
                    data-testid="delete-guardian-confirmation-name"
                  />
                </label>
                <Show when={guardianDeleteError()}>
                  {(message) => <p role="alert">{message()}</p>}
                </Show>
                <div class="settings-recovery-actions">
                  <button type="button" class="secondary" onClick={closeGuardianDelete}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="danger"
                    disabled={
                      deletingGuardian() ||
                      guardianConfirmationName().trim() !== guardian().displayName
                    }
                    onClick={() => void deleteGuardian()}
                  >
                    {deletingGuardian() ? 'Deleting…' : 'Delete guardian'}
                  </button>
                </div>
              </section>
            )}
          </Show>
          <Show when={props.onAddGuardian}>
            {(add) => (
              <button
                type="button"
                class="manage-users-add"
                onClick={() => add()()}
                data-testid="settings-add-guardian"
              >
                <span class="manage-users-add-glyph" aria-hidden="true">
                  +
                </span>
                <span>Add a guardian</span>
              </button>
            )}
          </Show>
        </Show>
      </div>
    </section>
  );
};

/** Guardian-only recovery surface for a deleted child. Password reset is part
 * of restoration so an old device can never sign the child back in. */
const RecentlyDeletedChildrenPanel: Component<{ sessionId: string }> = (props) => {
  const [children, { refetch }] = createResource(async () => {
    const result = await childRecoveryList({ sessionId: props.sessionId });
    return result.ok ? result.value : ([] as RecoverableChildEntry[]);
  });
  const [restoringId, setRestoringId] = createSignal<number | null>(null);
  const [displayName, setDisplayName] = createSignal('');
  const [newPassword, setNewPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const openRestore = (child: RecoverableChildEntry) => {
    setRestoringId(child.childAccountId);
    setDisplayName(child.displayName);
    setNewPassword('');
    setError(null);
  };
  const closeRestore = () => {
    setRestoringId(null);
    setDisplayName('');
    setNewPassword('');
    setError(null);
  };
  const restore = async (child: RecoverableChildEntry) => {
    setBusy(true);
    setError(null);
    try {
      const result = await childRestore({
        sessionId: props.sessionId,
        childAccountId: child.childAccountId,
        displayName: displayName(),
        newPassword: newPassword(),
      });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      closeRestore();
      await refetch();
    } finally {
      setBusy(false);
    }
  };
  const deletionDeadline = (value: string) =>
    new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(value),
    );

  return (
    <section class="settings-section" data-testid="settings-recently-deleted-children">
      <h2>Recently deleted children</h2>
      <p class="settings-section-intro">
        A deleted child can be reclaimed with their buckets, chores, goals, and money history for 30
        days. Reclaiming always requires a new child password.
      </p>
      <Show when={!children.loading} fallback={<p class="settings-section-empty">Loading…</p>}>
        <Show
          when={(children() ?? []).length > 0}
          fallback={<p class="settings-section-empty">No recently deleted child accounts.</p>}
        >
          <ul class="manage-users-list">
            <For each={children() ?? []}>
              {(child) => (
                <li class="manage-users-row manage-users-row--recovery">
                  <div>
                    <strong>{child.displayName}</strong>
                    <p class="settings-section-caption">
                      Available until {deletionDeadline(child.purgeAfterUtc)}
                    </p>
                  </div>
                  <Show
                    when={restoringId() === child.childAccountId}
                    fallback={
                      <button
                        type="button"
                        class="secondary"
                        onClick={() => openRestore(child)}
                        data-testid={`restore-child-${child.childAccountId}`}
                      >
                        Restore account
                      </button>
                    }
                  >
                    <div class="settings-recovery-form">
                      <label class="field">
                        <span class="field-label">Child name</span>
                        <input
                          type="text"
                          value={displayName()}
                          onInput={(event) => setDisplayName(event.currentTarget.value)}
                          autocomplete="off"
                        />
                      </label>
                      <label class="field">
                        <span class="field-label">New child password</span>
                        <input
                          type="password"
                          value={newPassword()}
                          onInput={(event) => setNewPassword(event.currentTarget.value)}
                          autocomplete="new-password"
                        />
                      </label>
                      <p class="settings-section-caption">
                        If another child now uses this name, choose a different name before
                        restoring.
                      </p>
                      <Show when={error()}>{(message) => <p role="alert">{message()}</p>}</Show>
                      <div class="settings-recovery-actions">
                        <button
                          type="button"
                          class="secondary"
                          onClick={closeRestore}
                          disabled={busy()}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="primary"
                          onClick={() => void restore(child)}
                          disabled={
                            busy() || displayName().trim() === '' || newPassword().length < 8
                          }
                        >
                          {busy() ? 'Restoring…' : 'Restore child'}
                        </button>
                      </div>
                    </div>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
    </section>
  );
};

/** Equal-peer recovery surface for deleted guardians. Restoring always rotates
 * the guardian's local password before access is re-enabled. */
const RecentlyDeletedGuardiansPanel: Component<{ sessionId: string }> = (props) => {
  const [guardians, { refetch }] = createResource(async () => {
    const result = await guardianRecoveryList({ sessionId: props.sessionId });
    return result.ok ? result.value : ([] as RecoverableGuardianEntry[]);
  });
  const [restoringId, setRestoringId] = createSignal<number | null>(null);
  const [displayName, setDisplayName] = createSignal('');
  const [newPassword, setNewPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const openRestore = (guardian: RecoverableGuardianEntry) => {
    setRestoringId(guardian.guardianAccountId);
    setDisplayName(guardian.displayName);
    setNewPassword('');
    setError(null);
  };
  const closeRestore = () => {
    setRestoringId(null);
    setDisplayName('');
    setNewPassword('');
    setError(null);
  };
  const restore = async (guardian: RecoverableGuardianEntry) => {
    setBusy(true);
    setError(null);
    try {
      const result = await guardianRestore({
        sessionId: props.sessionId,
        guardianAccountId: guardian.guardianAccountId,
        displayName: displayName(),
        newPassword: newPassword(),
      });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      closeRestore();
      await refetch();
    } finally {
      setBusy(false);
    }
  };
  const deletionDeadline = (value: string) =>
    new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(value),
    );

  return (
    <section class="settings-section" data-testid="settings-recently-deleted-guardians">
      <h2>Recently deleted guardians</h2>
      <p class="settings-section-intro">
        Either guardian can restore a deleted guardian and their child relationships for 30 days.
        Restoring requires a new guardian password.
      </p>
      <Show when={!guardians.loading} fallback={<p class="settings-section-empty">Loading…</p>}>
        <Show
          when={(guardians() ?? []).length > 0}
          fallback={<p class="settings-section-empty">No recently deleted guardian accounts.</p>}
        >
          <ul class="manage-users-list">
            <For each={guardians() ?? []}>
              {(guardian) => (
                <li class="manage-users-row manage-users-row--recovery">
                  <div>
                    <strong>{guardian.displayName}</strong>
                    <p class="settings-section-caption">
                      Available until {deletionDeadline(guardian.purgeAfterUtc)}
                    </p>
                  </div>
                  <Show
                    when={restoringId() === guardian.guardianAccountId}
                    fallback={
                      <button
                        type="button"
                        class="secondary"
                        onClick={() => openRestore(guardian)}
                        data-testid={`restore-guardian-${guardian.guardianAccountId}`}
                      >
                        Restore account
                      </button>
                    }
                  >
                    <div class="settings-recovery-form">
                      <label class="field">
                        <span class="field-label">Guardian name</span>
                        <input
                          type="text"
                          value={displayName()}
                          onInput={(event) => setDisplayName(event.currentTarget.value)}
                          autocomplete="off"
                        />
                      </label>
                      <label class="field">
                        <span class="field-label">New guardian password</span>
                        <input
                          type="password"
                          value={newPassword()}
                          onInput={(event) => setNewPassword(event.currentTarget.value)}
                          autocomplete="new-password"
                        />
                      </label>
                      <Show when={error()}>{(message) => <p role="alert">{message()}</p>}</Show>
                      <div class="settings-recovery-actions">
                        <button
                          type="button"
                          class="secondary"
                          onClick={closeRestore}
                          disabled={busy()}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="primary"
                          onClick={() => void restore(guardian)}
                          disabled={
                            busy() || displayName().trim() === '' || newPassword().length < 10
                          }
                        >
                          {busy() ? 'Restoring…' : 'Restore guardian'}
                        </button>
                      </div>
                    </div>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
    </section>
  );
};
